import React, { useState, useEffect, useRef } from 'react';
import './Header.css';

const Header = ({ children, homepageContext = null, activeTab, onTabChange, onLogoClick, loadingComponent, disableScrollShrink = false, forceCollapsed = false, dockedHeader = false }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const headerRef = useRef(null);
  const [headerHeight, setHeaderHeight] = useState(0);
  const isScrolledRef = useRef(false);
  const expandedHeightRef = useRef(0);

  // Close mobile menu when active tab changes
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [activeTab]);

  // Prevent background stacking issues when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.classList.add('mobile-menu-open');
    } else {
      document.body.classList.remove('mobile-menu-open');
    }
    return () => document.body.classList.remove('mobile-menu-open');
  }, [isMobileMenuOpen]);

  // Desktop results-bar dropdown: close it when clicking outside. (Mobile uses the
  // full-screen overlay with its own ✕, so this only applies on desktop widths.)
  useEffect(() => {
    if (!isMobileMenuOpen) return undefined;
    const onDocMouseDown = (e) => {
      if (window.innerWidth < 769) return;
      if (!e.target.closest('.docked-menu')) setIsMobileMenuOpen(false);
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [isMobileMenuOpen]);

  // Track header height via ResizeObserver so the spacer always matches.
  // Hysteresis in the scroll handler (collapse >100px, expand <30px) prevents
  // the old feedback loop, so we can safely track height in both states.
  useEffect(() => {
    const measureHeight = () => {
      if (headerRef.current) {
        const height = headerRef.current.offsetHeight + 20;
        if (!isScrolledRef.current) {
          expandedHeightRef.current = height;
        }
        setHeaderHeight(height);
      }
    };

    measureHeight();

    const resizeObserver = new ResizeObserver(measureHeight);

    if (headerRef.current) {
      resizeObserver.observe(headerRef.current);
    }

    window.addEventListener('resize', measureHeight);

    return () => {
      window.removeEventListener('resize', measureHeight);
      resizeObserver.disconnect();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  useEffect(() => {
    // Docked header (results view): a fixed-size slim bar that never resizes —
    // the page scrolls UNDER it, Google-style. No scroll-driven collapse at all.
    if (dockedHeader) {
      setIsScrolled(false);
      isScrolledRef.current = false;
      return undefined;
    }
    if (forceCollapsed) {
      setIsScrolled(true);
      isScrolledRef.current = true;
      return undefined;
    }
    if (disableScrollShrink) {
      setIsScrolled(false);
      isScrolledRef.current = false;
      return undefined;
    }

    // Hysteresis: collapse at 100px, expand only when back below 30px.
    // Prevents rapid toggling when content shifts near the threshold.
    const handleScroll = () => {
      const offset = window.scrollY;
      if (!isScrolledRef.current && offset > 100) {
        isScrolledRef.current = true;
        setIsScrolled(true);
      } else if (isScrolledRef.current && offset < 30) {
        isScrolledRef.current = false;
        setIsScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [disableScrollShrink, forceCollapsed, dockedHeader]);

  // Mobile: keep header visible when keyboard opens on search input
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    if (window.innerWidth > 768) return undefined;

    const isSearchInput = (target) => !!target?.closest?.('#header-search-container');

    const handleFocusIn = (event) => {
      if (isSearchInput(event.target)) {
        headerRef.current?.classList.add('keyboard-open');
      }
    };

    const handleFocusOut = () => {
      setTimeout(() => {
        if (!isSearchInput(document.activeElement)) {
          headerRef.current?.classList.remove('keyboard-open');
        }
      }, 0);
    };

    document.addEventListener('focusin', handleFocusIn);
    document.addEventListener('focusout', handleFocusOut);
    return () => {
      document.removeEventListener('focusin', handleFocusIn);
      document.removeEventListener('focusout', handleFocusOut);
    };
  }, []);

  const getHeroTitle = () => {
    switch (activeTab) {
      case 'contact':
        return 'צור קשר';
      case 'feedback':
        return 'משוב והצעות לשיפור';
      case 'about':
        return 'אודות אורבניקס';
      case 'project-promotion':
        return 'קידום פרויקטים';
      default:
        return 'כל המידע על הנכס שלך במקום אחד';
    }
  };

  // Nav links, shared between desktop nav, the mobile overlay, and the docked bar.
  const navButtons = (closeMenuAfter = false) => {
    const items = [
      { id: 'property', label: 'מידע על נכס' },
      { id: 'about', label: 'אודות' },
      { id: 'project-promotion', label: 'קידום פרויקטים' },
      { id: 'contact', label: 'צור קשר' },
      { id: 'feedback', label: 'משוב' },
    ];
    return items.map(({ id, label }) => (
      <button
        type="button"
        key={id}
        className={`nav-link ${activeTab === id ? 'active' : ''}`}
        onClick={() => { onTabChange && onTabChange(id); if (closeMenuAfter) setIsMobileMenuOpen(false); }}
      >
        {label}
      </button>
    ));
  };

  // Docked header: slim sticky bar for the results view — logo · compact search · nav
  // on one row, focus chips tuck beneath, page scrolls under it with no animation.
  if (dockedHeader) {
    return (
      <>
        {/* Spacer reserves exactly the fixed bar's height so the scroll-away
            controls strip (rendered in App, in normal flow) butts right against
            the bar with no gap. headerHeight is offsetHeight + 20, so subtract 20. */}
        <div style={{ height: headerHeight ? Math.max(headerHeight - 20, 56) : 60, background: 'var(--bg-header)' }} />
        <header
          ref={headerRef}
          className={`main-header docked ${isMobileMenuOpen ? 'menu-open' : ''}`}
        >
          <button
            className="mobile-menu-toggle"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label={isMobileMenuOpen ? 'סגור תפריט' : 'פתח תפריט'}
          >
            <span className={`hamburger-icon ${isMobileMenuOpen ? 'open' : ''}`}>
              <span></span>
              <span></span>
              <span></span>
            </span>
          </button>

          <div className="docked-row">
            <div
              className="logo-area"
              onClick={() => onLogoClick ? onLogoClick() : (onTabChange && onTabChange('property'))}
              style={{ cursor: 'pointer' }}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === 'Enter') (onLogoClick ? onLogoClick() : (onTabChange && onTabChange('property'))); }}
              title="חזרה לדף הבית"
            >
              <img src="/urbanix-logo.svg" alt="Urbanix Logo" className="logo" />
            </div>

            <div className="docked-search">
              {children}
            </div>

            {/* Desktop results bar: the nav collapses into a compact dropdown so
                the search stays prominent (Google / Airbnb / YouTube pattern). */}
            <div className="docked-menu">
              <button
                type="button"
                className="docked-menu-btn"
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                aria-expanded={isMobileMenuOpen}
                aria-haspopup="true"
              >
                <span>תפריט</span>
                <svg className={`docked-menu-chevron ${isMobileMenuOpen ? 'open' : ''}`} width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
                  <path d="M2.5 4.5 6 8l3.5-3.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
              <div className={`docked-menu-dropdown ${isMobileMenuOpen ? 'open' : ''}`}>
                {navButtons(true)}
              </div>
            </div>
          </div>

          <nav className={`mobile-nav-overlay ${isMobileMenuOpen ? 'open' : ''}`}>
            {navButtons(true)}
          </nav>

          {loadingComponent && (
            <div className="header-border-loading">
              {loadingComponent}
            </div>
          )}
        </header>
      </>
    );
  }

  return (
    <>
      {/* Static Spacer - Always present, always same height as expanded header */}
      {/* This prevents layout jumps when header becomes fixed/changes size */}
      <div 
        style={{ 
          height: disableScrollShrink ? 0 : (headerHeight ? headerHeight : 'auto'), 
          minHeight: disableScrollShrink ? 0 : (['contact', 'feedback', 'about', 'project-promotion'].includes(activeTab) ? '180px' : '300px') 
        }} 
      />
      
      <header 
        ref={headerRef}
        className={`main-header ${(disableScrollShrink && !isMobileMenuOpen) ? 'static-header' : ''} ${isScrolled ? 'scrolled' : ''} ${['contact', 'feedback', 'about', 'project-promotion'].includes(activeTab) ? 'compact-header' : ''} ${isMobileMenuOpen ? 'menu-open' : ''}`}
      >
        <button 
          className="mobile-menu-toggle"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          aria-label={isMobileMenuOpen ? "סגור תפריט" : "פתח תפריט"}
        >
          <span className={`hamburger-icon ${isMobileMenuOpen ? 'open' : ''}`}>
            <span></span>
            <span></span>
            <span></span>
          </span>
        </button>

        <div className="header-content">
        <div 
          className="logo-area" 
          onClick={() => onLogoClick ? onLogoClick() : (onTabChange && onTabChange('property'))}
          style={{ cursor: 'pointer' }}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => { if (e.key === 'Enter') (onLogoClick ? onLogoClick() : (onTabChange && onTabChange('property'))); }}
          title="חזרה לדף הבית"
        >
          <img src="/urbanix-logo.svg" alt="Urbanix Logo" className="logo" />
        </div>

        {/* Desktop Navigation - Hidden on mobile */}
        <nav className="main-nav desktop-nav">
          <button
            type="button"
            className={`nav-link ${activeTab === 'property' ? 'active' : ''}`}
            onClick={() => { onTabChange && onTabChange('property'); }}
          >
            מידע על נכס
          </button>
          <button
            type="button"
            className={`nav-link ${activeTab === 'about' ? 'active' : ''}`}
            onClick={() => { onTabChange && onTabChange('about'); }}
          >
            אודות
          </button>
          <button
            type="button"
            className={`nav-link ${activeTab === 'project-promotion' ? 'active' : ''}`}
            onClick={() => { onTabChange && onTabChange('project-promotion'); }}
          >
            קידום פרויקטים
          </button>
          <button
            type="button"
            className={`nav-link ${activeTab === 'contact' ? 'active' : ''}`}
            onClick={() => { onTabChange && onTabChange('contact'); }}
          >
            צור קשר
          </button>
          <button
            type="button"
            className={`nav-link ${activeTab === 'feedback' ? 'active' : ''}`}
            onClick={() => { onTabChange && onTabChange('feedback'); }}
          >
            משוב
          </button>
        </nav>
      </div>

      {/* Mobile Navigation Overlay - Outside header-content to avoid being hidden */}
      <nav className={`mobile-nav-overlay ${isMobileMenuOpen ? 'open' : ''}`}>
        <button
          type="button"
          className={`nav-link ${activeTab === 'property' ? 'active' : ''}`}
          onClick={() => { onTabChange && onTabChange('property'); setIsMobileMenuOpen(false); }}
        >
          מידע על נכס
        </button>
        <button
          type="button"
          className={`nav-link ${activeTab === 'about' ? 'active' : ''}`}
          onClick={() => { onTabChange && onTabChange('about'); setIsMobileMenuOpen(false); }}
        >
          אודות
        </button>
        <button
          type="button"
          className={`nav-link ${activeTab === 'project-promotion' ? 'active' : ''}`}
          onClick={() => { onTabChange && onTabChange('project-promotion'); setIsMobileMenuOpen(false); }}
        >
          קידום פרויקטים
        </button>
        <button
          type="button"
          className={`nav-link ${activeTab === 'contact' ? 'active' : ''}`}
          onClick={() => { onTabChange && onTabChange('contact'); setIsMobileMenuOpen(false); }}
        >
          צור קשר
        </button>
        <button
          type="button"
          className={`nav-link ${activeTab === 'feedback' ? 'active' : ''}`}
          onClick={() => { onTabChange && onTabChange('feedback'); setIsMobileMenuOpen(false); }}
        >
          משוב
        </button>
      </nav>

      <div className="hero-search-section">
        <div className="hero-text-group">
          <h1 className="hero-title">
            {getHeroTitle()}
          </h1>
          {!['contact', 'feedback', 'about', 'project-promotion'].includes(activeTab) && (
            <p className="hero-subtitle">
              הזינו כתובת וקבלו את כל המידע על הנכס: תוכניות והיתרים, התחדשות עירונית, מידע פיננסי והתראות על שינויים.
            </p>
          )}
        </div>
        
        {/* Injected search bar - only show if passed and activeTab isn't contact/feedback */}
        {/* Actually let's rely on parent passing null if needed, but here we just render children */}
        <div id="header-search-container">
            {children}
        </div>
        {homepageContext}
      </div>
      
      {/* Loading progress - integrated into header border */}
      {loadingComponent && (
        <div className="header-border-loading">
          {loadingComponent}
        </div>
      )}
      </header>
    </>
  );
};

export default Header;
